/**
 * RequestApprovalTypeFormLayout
 *
 * Custom form layout for RequestApprovalType entities with progressive disclosure.
 *
 * Collapsible sections:
 *   - Identity (always): DisplayName, Description, Tags
 *   - Approval Configuration (always): Type, DurationDays, ActorThreshold, AllowMultipleRequests
 *   - System Type Banner (when IsSystem): informational alert
 *   - Possessive Configuration (when Type = Possessive): PossessiveScope, PossessiveTargetAttribute,
 *     PossessiveActorsAttribute, PossessiveActorsAttributes — delegates to existing renderers
 *   - Explicit Actors (always): ExplicitActors
 *   - Email Templates (always, collapsed): CompletedApprovalEmail, PendingApprovalEmail,
 *     RequestCreatedEmail, RejectedRequestEmail, ExpiredEmail
 *   - System Fields (admin only): Id, EntityType, IsSystem
 */

import React, { useState } from 'react';
import { Alert, Box, Text, Collapse, UnstyledButton, Group } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconShieldLock } from '@tabler/icons-react';
import { getEntityValueFromARef } from '../../../types';
import type { FormAttribute } from '../EntityUpsertForm.types';
import { getAttributeName } from '../../../utils/schemaAttributeUtils';

export interface RequestApprovalTypeFormLayoutProps {
    schemaAttributes: FormAttribute[];
    formData: Record<string, any>;
    onFieldChange: (fieldName: string, value: any) => void;
    renderField: (attr: FormAttribute, value: unknown, onChange: (value: unknown) => void) => React.ReactNode;
    disabled: boolean;
    isStandard?: boolean;
}

function normKey(raw: string): string {
    return (raw ?? '').toString().trim().toLowerCase().replace(/^\$\./, '');
}

function findAttr(attrs: FormAttribute[], key: string): FormAttribute | undefined {
    return attrs.find(a => normKey((a.name ?? a.id ?? '').toString()) === key);
}

function renderOneField(
    attr: FormAttribute,
    formData: Record<string, any>,
    onFieldChange: (fieldName: string, value: any) => void,
    renderField: RequestApprovalTypeFormLayoutProps['renderField'],
): React.ReactNode {
    const name = getAttributeName(attr);
    const value = name ? getEntityValueFromARef(name, formData as Record<string, unknown>) : '';
    const handleChange = (v: unknown) => onFieldChange(name, v);
    return renderField(attr, value ?? '', handleChange);
}

function resolveType(formData: Record<string, any>): string {
    const t = formData.Type ?? formData.type ?? '';
    if (typeof t === 'string') return t.toLowerCase();
    if (t && typeof t === 'object') {
        const val = t.DisplayName || t.displayName || t.Id || t.id || '';
        return (val as string).toLowerCase();
    }
    return '';
}

function resolveIsSystem(formData: Record<string, any>): boolean {
    const raw = formData.IsSystem ?? formData.isSystem;
    if (typeof raw === 'boolean') return raw;
    if (typeof raw === 'string') return raw.toLowerCase() === 'true';
    return !!raw;
}

const IDENTITY_FIELDS = ['displayname', 'description', 'tags'];
const APPROVAL_CONFIG_FIELDS = ['type', 'durationdays', 'actorthreshold', 'allowmultiplerequests'];
const POSSESSIVE_FIELDS = ['possessivescope', 'possessivetargetattribute', 'possessiveactorsattribute', 'possessiveactorsattributes'];
const EXPLICIT_ACTORS_FIELDS = ['explicitactors'];
const EMAIL_TEMPLATE_FIELDS = ['completedapprovalemail', 'pendingapprovalemail', 'requestcreatedemail', 'rejectedrequestemail', 'expiredemail'];
const SYSTEM_FIELDS = ['id', 'entitytype', 'issystem'];

const ALL_CATEGORIZED = new Set([
    ...IDENTITY_FIELDS, ...APPROVAL_CONFIG_FIELDS, ...POSSESSIVE_FIELDS,
    ...EXPLICIT_ACTORS_FIELDS, ...EMAIL_TEMPLATE_FIELDS, ...SYSTEM_FIELDS,
]);

function CollapsibleSection({
    title,
    defaultOpen = true,
    children,
}: {
    title: string;
    defaultOpen?: boolean;
    children: React.ReactNode;
}): React.ReactElement {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <Box mb="sm">
            <UnstyledButton
                onClick={() => setOpen(o => !o)}
                w="100%"
                py="xs"
            >
                <Group gap="xs" align="center">
                    {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                    <Text fw={600} size="sm" c="dimmed">{title}</Text>
                </Group>
            </UnstyledButton>
            <Collapse in={open}>
                <Box mt={4}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

function renderFieldGroup(
    attrs: FormAttribute[],
    formData: Record<string, any>,
    onFieldChange: (fieldName: string, value: any) => void,
    renderField: RequestApprovalTypeFormLayoutProps['renderField'],
): React.ReactNode {
    if (attrs.length === 0) return null;
    return (
        <>
            {attrs.map((attr, i) => (
                <Box key={(attr.name ?? attr.id ?? `fg-${i}`).toString()} mb="xs">
                    {renderOneField(attr, formData, onFieldChange, renderField)}
                </Box>
            ))}
        </>
    );
}

function collectAttrs(schemaAttributes: FormAttribute[], keys: string[]): FormAttribute[] {
    const result: FormAttribute[] = [];
    for (const key of keys) {
        const attr = findAttr(schemaAttributes, key);
        if (attr) result.push(attr);
    }
    return result;
}

export const RequestApprovalTypeFormLayout: React.FC<RequestApprovalTypeFormLayoutProps> = ({
    schemaAttributes,
    formData,
    onFieldChange,
    renderField,
    disabled: _disabled,
    isStandard = false,
}) => {
    const typeLower = resolveType(formData);
    const isPossessive = typeLower === 'possessive';
    const isSystem = resolveIsSystem(formData);

    const identityAttrs = collectAttrs(schemaAttributes, IDENTITY_FIELDS);
    const approvalConfigAttrs = collectAttrs(schemaAttributes, APPROVAL_CONFIG_FIELDS);
    const possessiveAttrs = collectAttrs(schemaAttributes, POSSESSIVE_FIELDS);
    const explicitActorsAttrs = collectAttrs(schemaAttributes, EXPLICIT_ACTORS_FIELDS);
    const emailTemplateAttrs = collectAttrs(schemaAttributes, EMAIL_TEMPLATE_FIELDS);
    const systemAttrs = collectAttrs(schemaAttributes, SYSTEM_FIELDS);

    const uncategorized = schemaAttributes.filter(a => {
        const key = normKey((a.name ?? a.id ?? '').toString());
        return !ALL_CATEGORIZED.has(key);
    });

    return (
        <Box>
            {/* System Type Banner */}
            {isSystem && (
                <Alert
                    color="blue"
                    variant="light"
                    icon={<IconShieldLock size={18} />}
                    mb="md"
                    title="System Approval Type"
                >
                    This is a system-managed approval type. Core fields may be locked by the backend and should not be modified.
                </Alert>
            )}

            {/* Identity */}
            {identityAttrs.length > 0 && (
                <CollapsibleSection title="Identity" defaultOpen>
                    {renderFieldGroup(identityAttrs, formData, onFieldChange, renderField)}
                </CollapsibleSection>
            )}

            {/* Approval Configuration */}
            {approvalConfigAttrs.length > 0 && (
                <CollapsibleSection title="Approval Configuration" defaultOpen>
                    {renderFieldGroup(approvalConfigAttrs, formData, onFieldChange, renderField)}
                </CollapsibleSection>
            )}

            {/* Possessive Configuration -- visible when Type = Possessive */}
            {isPossessive && possessiveAttrs.length > 0 && (
                <CollapsibleSection title="Possessive Configuration" defaultOpen>
                    {renderFieldGroup(possessiveAttrs, formData, onFieldChange, renderField)}
                </CollapsibleSection>
            )}

            {/* Explicit Actors */}
            {explicitActorsAttrs.length > 0 && (
                <CollapsibleSection title="Explicit Actors" defaultOpen={false}>
                    {renderFieldGroup(explicitActorsAttrs, formData, onFieldChange, renderField)}
                </CollapsibleSection>
            )}

            {/* Email Templates */}
            {emailTemplateAttrs.length > 0 && (
                <CollapsibleSection title="Email Templates" defaultOpen={false}>
                    {renderFieldGroup(emailTemplateAttrs, formData, onFieldChange, renderField)}
                </CollapsibleSection>
            )}

            {/* System Fields -- admin only */}
            {!isStandard && systemAttrs.length > 0 && (
                <CollapsibleSection title="System Fields" defaultOpen={false}>
                    {renderFieldGroup(systemAttrs, formData, onFieldChange, renderField)}
                </CollapsibleSection>
            )}

            {/* Uncategorized attributes -- admin only */}
            {!isStandard && uncategorized.length > 0 && (
                <CollapsibleSection title="Other Attributes" defaultOpen={false}>
                    {renderFieldGroup(uncategorized, formData, onFieldChange, renderField)}
                </CollapsibleSection>
            )}
        </Box>
    );
};
